import { ChangeEvent } from 'react';
import { TFunction } from 'i18next';
import { ApolloError } from '@apollo/client';
import { FormErrors, FormState, CreateRequestType } from './BusinessPage.types';

export const getPhoneError = (phone: string, t: TFunction): string | null => {
  const cleanedPhone = phone.replace(/\D/g, '');
  const opCode = phone.match(/\((\d{3})\)/)?.[1] || null;

  if (!cleanedPhone) {
    return t('business.errors.phone');
  }

  if (cleanedPhone.length < 10) {
    return t('business.errors.short_phone');
  }

  if (opCode) {
    const validOpCodeRegex = /^(010|050|051|055|060|070|077|099)$/;

    if (!validOpCodeRegex.test(opCode)) {
      return t('business.errors.wrong_operator');
    }
  }

  if (!/^(\+?\d{1,3})?(\d{10,15})$/.test(cleanedPhone)) {
    return t('business.errors.invalid_phone');
  }

  return null;
};

export const isEmail = (email: string): boolean =>
  /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(email);

export const handleChange = (
  e: ChangeEvent<HTMLInputElement>,
  setErrors: React.Dispatch<React.SetStateAction<FormErrors>>,
  setState: React.Dispatch<React.SetStateAction<FormState>>,
  getPhoneErrorFn: (phone: string, t: TFunction) => string | null,
  t: TFunction
) => {
  const { name, value } = e.target;

  const fixedValue = value
    .trim()
    .replace(/\s{2,}/g, ' ')
    .replace(/'{2,}/g, "'");

  if (name === 'phone') {
    const phoneError = getPhoneErrorFn(fixedValue, t);
    setErrors(prev => ({ ...prev, phone: phoneError || '' }));
  } else if (name === 'email' && isEmail(fixedValue)) {
    setErrors(prev => ({ ...prev, email: '' }));
  } else if (name === 'name' && fixedValue.length >= 3) {
    setErrors(prev => ({ ...prev, name: '' }));
  } else if (name === 'shop' && fixedValue.length >= 3) {
    setErrors(prev => ({ ...prev, shop: '' }));
  }

  setState(prevState => ({ ...prevState, [name]: fixedValue }));
};

export const createPhoneMask = () => {
  const phoneMask = (rawValue = '') => {
    let mask = [
      /\d/,
      /\d/,
      ')',
      ' ',
      /\d/,
      /\d/,
      /\d/,
      '-',
      /\d/,
      /\d/,
      '-',
      /\d/,
      /\d/,
    ];
    let prefix = ['(', /[0-9]/];
    if (rawValue[0] === '(' && /[1-9]/.test(rawValue[1])) {
      prefix = ['(', /\d/];
    }
    mask = prefix.concat(mask);
    return mask;
  };
  return phoneMask;
};

export const validateAndSubmitForm = (
  e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  errors: FormErrors,
  state: FormState,
  setErrors: React.Dispatch<React.SetStateAction<FormErrors>>,
  t: TFunction,
  createRequest: CreateRequestType,
  setShowResultModal: React.Dispatch<React.SetStateAction<boolean>>
) => {
  e.preventDefault();
  const inputErrors: FormErrors = {
    shop: '',
    name: '',
    categories: '',
    phone: '',
    email: '',
  };

  if (state.email.length > 0 && !isEmail(state.email)) {
    inputErrors.email = t('business.errors.email');
  }

  if (!state.name.length) {
    inputErrors.name = t('business.errors.name');
  } else if (state.name.length < 3) {
    inputErrors.name = t('business.errors.error_length');
  }

  if (!state.categories.length) {
    inputErrors.categories = t('business.errors.category');
  }

  const phoneError = getPhoneError(state.phone, t);
  if (phoneError) {
    inputErrors.phone = phoneError;
  } else if (!state.phone) {
    inputErrors.phone = t('business.errors.phone');
  }

  if (!state.shop.length) {
    inputErrors.shop = t('business.errors.shop');
  } else if (state.shop.length < 3) {
    inputErrors.shop = t('business.errors.error_length');
  }

  setErrors(prevErrors => ({
    ...prevErrors,
    ...inputErrors,
  }));

  const isValid = Object.values(inputErrors).every(error => !error);
  if (isValid) {
    createRequest({
      variables: {
        name: state.name,
        shopName: state.shop,
        phone: state.phone.replace(/\D/g, ''),
        email: state.email,
        categoriesIds: state.categories,
      },
    })
      .then(response => {
        response.data?.createOnboardingRequest.entity &&
          setShowResultModal(true);
      })
      .catch((error: ApolloError) => {
        if (error.graphQLErrors) {
          const serverErrors: FormErrors = { ...inputErrors };

          error.graphQLErrors.forEach(
            (graphqlError: {
              message: string;
              path?: string | ReadonlyArray<string | number> | undefined;
            }) => {
              const { message, path } = graphqlError;
              serverErrors[path as keyof FormErrors] = message;
            }
          );

          setErrors(serverErrors);
        } else {
          console.error('Unexpected error:', error);
        }
      });
  }
};

export const handleCategories = (
  e: ChangeEvent<HTMLInputElement>,
  state: FormState,
  setState: React.Dispatch<React.SetStateAction<FormState>>,
  setCategoriesNamesArr: React.Dispatch<React.SetStateAction<string[]>>,
  setErrors: React.Dispatch<React.SetStateAction<FormErrors>>
) => {
  const { name, value, id, checked } = e.target;

  if (checked) {
    setCategoriesNamesArr(prevArr => [...prevArr, value]);
    setState(prevState => ({
      ...prevState,
      [name]: [...prevState.categories, id],
    }));
  } else {
    const filteredCategories = state.categories.filter(item => item !== id);
    setCategoriesNamesArr(prevArr => prevArr.filter(item => item !== value));
    setState(prevState => ({
      ...prevState,
      [name]: filteredCategories,
    }));
  }

  if (state.categories.length > 0 || checked) {
    setErrors(prevErrors => ({ ...prevErrors, categories: '' }));
  }
};
